import { useEffect, useMemo, useState } from "react";
import { useForm, type SubmitHandler, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileText, Image, Plus, Trash2, Smartphone, Monitor, ChevronDown, ChevronUp } from "lucide-react";
import {
  useCreateHeroCarousel,
  useUpdateHeroCarousel,
} from "@admin/hooks/useAdminCarousels";
import type { HeroCarouselRecord } from "@shared/types/carousel";
import {
  ImageUpload,
  type UploadedImage,
} from "@shared/components/ImageUpload";
import { ModalWithTabs, type Tab } from "@admin/components/ui/ModalWithTabs";

const optionalString = (max: number, message: string) =>
  z
    .string()
    .max(max, message)
    .optional()
    .transform((value) => {
      if (value === undefined) return undefined;
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    });

const optionalUrl = z
  .union([z.string().url("Debe ser una URL válida"), z.literal(""), z.undefined()])
  .transform((value) => {
    if (!value || typeof value !== "string") return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  });

const heroSlideSchema = z.object({
  mobileImageUrl: z.string().url("Debes subir una imagen móvil válida"),
  webImageUrl: z.string().url("Debes subir una imagen web válida"),
  title: optionalString(100, "El título no puede superar los 100 caracteres"),
  description: optionalString(200, "La descripción no puede superar los 200 caracteres"),
  ctaText: optionalString(50, "El texto del CTA no puede superar los 50 caracteres"),
  linkUrl: optionalUrl,
  order: z.number().int().min(0),
});

const heroCarouselSchema = z.object({
  name: z
    .string()
    .min(3, "El nombre debe tener al menos 3 caracteres")
    .max(120, "El nombre no puede superar los 120 caracteres")
    .transform((value) => value.trim()),
  description: optionalString(
    320,
    "La descripción no puede superar los 320 caracteres"
  ),
  items: z
    .array(heroSlideSchema)
    .min(1, "Debes agregar al menos un slide"),
  seoTitle: optionalString(60, "El título SEO no puede superar los 60 caracteres"),
  seoDescription: optionalString(160, "La descripción SEO no puede superar los 160 caracteres"),
  seoKeywords: optionalString(200, "Las palabras clave SEO no pueden superar los 200 caracteres"),
  isActive: z.boolean(),
});

type HeroCarouselFormInput = z.input<typeof heroCarouselSchema>;
type HeroCarouselFormOutput = z.output<typeof heroCarouselSchema>;

interface HeroCarouselFormProps {
  initialData?: HeroCarouselRecord;
  existingCarousels?: HeroCarouselRecord[];
  onClose: () => void;
  onSuccess: (message: { title: string; description: string }) => void;
  onError: (message: { title: string; description: string }) => void;
}

interface SlideImageState {
  mobile: UploadedImage[];
  web: UploadedImage[];
}

// Mapeo de field.id a imágenes (más estable que índices)
type SlideImagesMap = Map<string, SlideImageState>;

export function HeroCarouselForm({
  initialData,
  existingCarousels = [],
  onClose,
  onSuccess,
  onError,
}: HeroCarouselFormProps) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setValue,
    setError,
    watch,
  } = useForm<HeroCarouselFormInput>({
    resolver: zodResolver(heroCarouselSchema),
    defaultValues: {
      name: initialData?.name ?? "",
      description: initialData?.description ?? "",
      items: initialData?.items?.length
        ? initialData.items.map((item, index) => ({
            mobileImageUrl: item.mobileImageUrl,
            webImageUrl: item.webImageUrl,
            title: item.title ?? "",
            description: item.description ?? "",
            ctaText: item.ctaText ?? "",
            linkUrl: item.linkUrl ?? "",
            order: item.order ?? index,
          }))
        : [
            {
              mobileImageUrl: "",
              webImageUrl: "",
              title: "",
              description: "",
              ctaText: "",
              linkUrl: "",
              order: 0,
            },
          ],
      seoTitle: initialData?.seoTitle ?? "",
      seoDescription: initialData?.seoDescription ?? "",
      seoKeywords: initialData?.seoKeywords ?? "",
      isActive: initialData?.isActive ?? true,
    },
    mode: "onChange",
  });

  const { fields, append } = useFieldArray({
    control,
    name: "items",
  });

  // Estado para controlar qué slides están expandidos (usando field.id)
  const [expandedSlides, setExpandedSlides] = useState<Set<string>>(() => {
    // Se inicializará después de que fields esté disponible
    return new Set<string>();
  });

  // Estado para las imágenes de cada slide (usando field.id como clave)
  const [slideImages, setSlideImages] = useState<SlideImagesMap>(() => {
    const map = new Map<string, SlideImageState>();
    return map;
  });

  // Inicializar slideImages y expandedSlides cuando fields cambia
  useEffect(() => {
    if (fields.length === 0) return;

    setSlideImages((prev) => {
      const newMap = new Map(prev);
      
      // Para cada field, verificar si ya tiene imágenes o inicializar desde initialData
      fields.forEach((field, index) => {
        if (!newMap.has(field.id)) {
          // Si no existe, intentar obtener desde initialData
          const initialItem = initialData?.items?.[index];
          if (initialItem) {
            newMap.set(field.id, {
              mobile: initialItem.mobileImageUrl
                ? [
                    {
                      id: `mobile-${initialData.id}-${field.id}`,
                      url: initialItem.mobileImageUrl,
                      publicId: initialItem.mobileImageUrl,
                      fileName: `mobile-${initialItem.title || "slide"}`,
                      alt: initialItem.title || "Mobile image",
                    },
                  ]
                : [],
              web: initialItem.webImageUrl
                ? [
                    {
                      id: `web-${initialData.id}-${field.id}`,
                      url: initialItem.webImageUrl,
                      publicId: initialItem.webImageUrl,
                      fileName: `web-${initialItem.title || "slide"}`,
                      alt: initialItem.title || "Web image",
                    },
                  ]
                : [],
            });
          } else {
            // Si no hay initialData, inicializar vacío
            newMap.set(field.id, { mobile: [], web: [] });
          }
        }
      });

      // Eliminar imágenes de fields que ya no existen
      const currentFieldIds = new Set(fields.map((f) => f.id));
      Array.from(newMap.keys()).forEach((fieldId) => {
        if (!currentFieldIds.has(fieldId)) {
          newMap.delete(fieldId);
        }
      });

      return newMap;
    });

    // Inicializar expandedSlides
    setExpandedSlides((prev) => {
      const newSet = new Set(prev);
      // Si es la primera vez, expandir todos
      if (prev.size === 0) {
        fields.forEach((field) => newSet.add(field.id));
      } else {
        // Mantener solo los que aún existen
        const currentFieldIds = new Set(fields.map((f) => f.id));
        Array.from(newSet).forEach((fieldId) => {
          if (!currentFieldIds.has(fieldId)) {
            newSet.delete(fieldId);
          }
        });
      }
      return newSet;
    });
  }, [fields, initialData]);

  const watchedItems = watch("items");

  // Sincronizar imágenes con el formulario usando field.id
  useEffect(() => {
    fields.forEach((field, index) => {
      const images = slideImages.get(field.id);
      const item = watchedItems[index];
      if (images && item) {
        if (images.mobile[0]?.url !== item.mobileImageUrl) {
          setValue(`items.${index}.mobileImageUrl`, images.mobile[0]?.url || "", {
            shouldValidate: true,
          });
        }
        if (images.web[0]?.url !== item.webImageUrl) {
          setValue(`items.${index}.webImageUrl`, images.web[0]?.url || "", {
            shouldValidate: true,
          });
        }
      }
    });
  }, [slideImages, watchedItems, setValue, fields]);

  const { mutateAsync: createHeroCarousel, isPending: isCreating } =
    useCreateHeroCarousel();
  const { mutateAsync: updateHeroCarousel, isPending: isUpdating } =
    useUpdateHeroCarousel();

  const handleSlideImagesChange = (
    fieldId: string,
    type: "mobile" | "web",
    images: UploadedImage[]
  ) => {
    setSlideImages((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(fieldId) || { mobile: [], web: [] };
      newMap.set(fieldId, {
        ...current,
        [type]: images,
      });
      return newMap;
    });
  };

  const handleRemoveSlide = (index: number) => {
    // Obtener el array actual de items
    const currentItems = watchedItems;
    
    // Validar que el índice sea válido
    if (index < 0 || index >= currentItems.length) return;

    // Obtener el field.id del elemento que vamos a eliminar ANTES de filtrar
    const fieldToRemove = fields[index];
    const fieldIdToRemove = fieldToRemove?.id;

    // Filtrar el array: devolver todos los elementos excepto el del índice coincidente
    const newItems = currentItems.filter((_, idx) => idx !== index);

    // Reindexar el orden de los slides restantes
    const reindexedItems = newItems.map((item, newIndex) => ({
      ...item,
      order: newIndex,
    }));

    // Reemplazar todo el array con el nuevo array filtrado
    setValue("items", reindexedItems, { shouldValidate: true });

    // Actualizar slideImages: eliminar solo el slide eliminado (los demás field.id no cambian)
    if (fieldIdToRemove) {
      setSlideImages((prev) => {
        const newMap = new Map(prev);
        newMap.delete(fieldIdToRemove);
        return newMap;
      });
    }

    // Actualizar expandedSlides: eliminar solo el slide eliminado
    if (fieldIdToRemove) {
      setExpandedSlides((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldIdToRemove);
        return newSet;
      });
    }
  };

  const handleAddSlide = () => {
    const newIndex = fields.length;
    append({
      mobileImageUrl: "",
      webImageUrl: "",
      title: "",
      linkUrl: "",
      order: newIndex,
    });
    // Las imágenes se inicializarán en el useEffect cuando fields cambie
    // Expandir el nuevo slide se manejará en el useEffect también
  };

  const toggleSlideExpand = (fieldId: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setExpandedSlides((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fieldId)) {
        newSet.delete(fieldId);
      } else {
        newSet.add(fieldId);
      }
      return newSet;
    });
  };

  const existingNames = useMemo(() => {
    return (existingCarousels ?? [])
      .filter(
        (carousel): carousel is HeroCarouselRecord =>
          Boolean(carousel) && carousel.id !== initialData?.id && Boolean(carousel.name)
      )
      .map((carousel) => carousel.name!.toLowerCase());
  }, [existingCarousels, initialData?.id]);

  const onSubmit: SubmitHandler<HeroCarouselFormInput> = async (formValues) => {
    const parsed: HeroCarouselFormOutput = heroCarouselSchema.parse(formValues);

    if (existingNames.includes(parsed.name.toLowerCase())) {
      setError("name", {
        type: "manual",
        message: "Ya existe un carrusel hero con este nombre",
      });
      return;
    }

    try {
      const payload = {
        name: parsed.name,
        description: parsed.description ?? undefined,
        items: parsed.items.map((item, index) => ({
          mobileImageUrl: item.mobileImageUrl,
          webImageUrl: item.webImageUrl,
          title: item.title ?? undefined,
          description: item.description ?? undefined,
          ctaText: item.ctaText ?? undefined,
          linkUrl: item.linkUrl ?? undefined,
          order: index,
        })),
        seoTitle: parsed.seoTitle ?? undefined,
        seoDescription: parsed.seoDescription ?? undefined,
        seoKeywords: parsed.seoKeywords ?? undefined,
        isActive: parsed.isActive,
      };

      if (initialData) {
        await updateHeroCarousel({
          id: initialData.id,
          data: payload,
        });
        onSuccess({
          title: "Carrusel hero actualizado",
          description: `Se actualizó "${parsed.name}" correctamente.`,
        });
      } else {
        await createHeroCarousel(payload);
        onSuccess({
          title: "Carrusel hero creado",
          description: `Se creó "${parsed.name}" correctamente.`,
        });
      }

      onClose();
    } catch (error) {
      onError({
        title: "Error al guardar",
        description:
          "No se pudo guardar el carrusel hero. Revisa los datos e intenta nuevamente.",
      });
    }
  };

  // Definir los tabs
  const tabs: Tab[] = [
    {
      id: "general",
      label: "General",
      icon: <FileText className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del Carrusel <span className="text-red-500">*</span>
              </label>
              <input
                {...register("name")}
                type="text"
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                  errors.name
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Ej: Promoción verano 2025"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción
              </label>
              <textarea
                {...register("description")}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                  errors.description
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Descripción del carrusel hero"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.description.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título SEO
                </label>
                <input
                  {...register("seoTitle")}
                  type="text"
                  maxLength={60}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                    errors.seoTitle
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Título SEO (máx. 60 caracteres)"
                />
                {errors.seoTitle && (
                  <p className="mt-1 text-sm text-red-600">{errors.seoTitle.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Palabras clave SEO
                </label>
                <input
                  {...register("seoKeywords")}
                  type="text"
                  maxLength={200}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                    errors.seoKeywords
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Palabras clave separadas por comas"
                />
                {errors.seoKeywords && (
                  <p className="mt-1 text-sm text-red-600">{errors.seoKeywords.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción SEO
              </label>
              <textarea
                {...register("seoDescription")}
                rows={2}
                maxLength={160}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                  errors.seoDescription
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Descripción SEO (máx. 160 caracteres)"
              />
              {errors.seoDescription && (
                <p className="mt-1 text-sm text-red-600">{errors.seoDescription.message}</p>
              )}
            </div>

            <label className="inline-flex items-center space-x-2 text-sm text-gray-700">
              <input
                type="checkbox"
                {...register("isActive")}
                className="h-4 w-4 text-[#282828] border-gray-300 rounded focus:ring-[#282828]"
              />
              <span>Marcar como activo</span>
            </label>
          </div>
        </div>
      ),
    },
    {
      id: "images",
      label: "Slides",
      icon: <Image className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          {fields.map((field, index) => {
            const slideImagesState = slideImages.get(field.id) || { mobile: [], web: [] };
            const slideErrors = errors.items?.[index];
            const isExpanded = expandedSlides.has(field.id);
            const slideTitle = watchedItems[index]?.title || `Slide ${index + 1}`;

            return (
              <div
                key={field.id}
                className="border border-gray-200 rounded-lg bg-white overflow-hidden"
              >
                {/* Header colapsable */}
                <button
                  type="button"
                  onClick={(e) => toggleSlideExpand(field.id, e)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    )}
                    <div className="text-left">
                      <h3 className="text-sm font-semibold text-gray-800">
                        {slideTitle}
                      </h3>
                      {!isExpanded && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {slideImagesState.mobile[0]?.url && slideImagesState.web[0]?.url
                            ? "Imágenes cargadas"
                            : "Sin imágenes"}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSlide(index);
                        }}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                        title="Eliminar slide"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </button>

                {/* Contenido colapsable */}
                {isExpanded && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Título del slide */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Título del slide
                    </label>
                    <input
                      {...register(`items.${index}.title`)}
                      type="text"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                        slideErrors?.title
                          ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Ej: Promoción verano"
                      maxLength={100}
                    />
                    {slideErrors?.title && (
                      <p className="mt-1 text-sm text-red-600">
                        {slideErrors.title.message}
                      </p>
                    )}
                  </div>

                  {/* Descripción del slide */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción del slide
                    </label>
                    <textarea
                      {...register(`items.${index}.description`)}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                        slideErrors?.description
                          ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Ej: Descripción detallada de la promoción"
                      maxLength={200}
                    />
                    {slideErrors?.description && (
                      <p className="mt-1 text-sm text-red-600">
                        {slideErrors.description.message}
                      </p>
                    )}
                  </div>

                  {/* CTA Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Texto del botón (CTA)
                    </label>
                    <input
                      {...register(`items.${index}.ctaText`)}
                      type="text"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                        slideErrors?.ctaText
                          ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="Ej: Ver más"
                      maxLength={50}
                    />
                    {slideErrors?.ctaText && (
                      <p className="mt-1 text-sm text-red-600">
                        {slideErrors.ctaText.message}
                      </p>
                    )}
                  </div>

                  {/* URL del slide */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL del slide
                    </label>
                    <input
                      {...register(`items.${index}.linkUrl`)}
                      type="url"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] ${
                        slideErrors?.linkUrl
                          ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300"
                      }`}
                      placeholder="https://ejemplo.com/promocion"
                    />
                    {slideErrors?.linkUrl && (
                      <p className="mt-1 text-sm text-red-600">
                        {slideErrors.linkUrl.message}
                      </p>
                    )}
                  </div>

                  {/* Imagen Mobile */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Smartphone className="w-4 h-4 inline mr-1" />
                      Imagen Mobile <span className="text-red-500">*</span>
                    </label>
                    <ImageUpload
                      uploadedImages={slideImagesState.mobile}
                      onImagesUploaded={(images) =>
                        handleSlideImagesChange(field.id, "mobile", images)
                      }
                      onImageRemoved={() =>
                        handleSlideImagesChange(field.id, "mobile", [])
                      }
                      maxFiles={1}
                      maxSizeMb={5}
                    />
                    {slideErrors?.mobileImageUrl && (
                      <p className="mt-1 text-sm text-red-600">
                        {slideErrors.mobileImageUrl.message}
                      </p>
                    )}
                  </div>

                  {/* Imagen Web */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Monitor className="w-4 h-4 inline mr-1" />
                      Imagen Web/Desktop <span className="text-red-500">*</span>
                    </label>
                    <ImageUpload
                      uploadedImages={slideImagesState.web}
                      onImagesUploaded={(images) =>
                        handleSlideImagesChange(field.id, "web", images)
                      }
                      onImageRemoved={() =>
                        handleSlideImagesChange(field.id, "web", [])
                      }
                      maxFiles={1}
                      maxSizeMb={5}
                    />
                    {slideErrors?.webImageUrl && (
                      <p className="mt-1 text-sm text-red-600">
                        {slideErrors.webImageUrl.message}
                      </p>
                    )}
                  </div>
                </div>
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={handleAddSlide}
            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#282828] hover:bg-[#282828]/5 transition-colors flex items-center justify-center space-x-2 text-gray-600 hover:text-[#282828]"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">Agregar Slide</span>
          </button>
        </div>
      ),
    },
  ];

  // Determinar qué tabs tienen errores
  const tabErrors = useMemo(() => {
    const generalFields = ["name", "description", "seoTitle", "seoDescription", "seoKeywords", "isActive"];
    const imagesFields = ["items"];

    const hasGeneralErrors = generalFields.some((field) => errors[field as keyof typeof errors]);
    const hasImagesErrors = imagesFields.some((field) => errors[field as keyof typeof errors]);

    return {
      general: hasGeneralErrors,
      images: hasImagesErrors,
    };
  }, [errors]);

  // Footer con botones
  const footer = (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
        disabled={isSubmitting || isCreating || isUpdating}
      >
        Cancelar
      </button>
      <button
        type="submit"
        form="hero-carousel-form"
        disabled={isSubmitting || isCreating || isUpdating}
        className="px-4 py-2 text-white bg-[#282828] border border-transparent rounded-md hover:bg-[#3a3a3a] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {isSubmitting || isCreating || isUpdating
          ? "Guardando..."
          : initialData
            ? "Guardar cambios"
            : "Crear carrusel"}
      </button>
    </div>
  );

  return (
    <ModalWithTabs
      isOpen={true}
      onClose={onClose}
      title={initialData ? "Editar Carrusel Hero" : "Crear Carrusel Hero"}
      tabs={tabs}
      size="xl"
      footer={footer}
      defaultTab="general"
      formId="hero-carousel-form"
      onSubmit={handleSubmit(onSubmit)}
      tabErrors={tabErrors}
    />
  );
}
